import { getAllSpecs, getFullDemoPath, type SpecEntry } from '@linkode/shared';
import { useTypedTranslation } from '../i18n';
import { Card, Tag, StatCard } from '../stories/landing';
import '../stories/landing/landing.css';

/**
 * Progress Board page displaying completed Specs
 * Requirements: 6.1, 6.3
 */
export function ProgressPage() {
  const { t } = useTypedTranslation();
  const specs = getAllSpecs();
  const includeStaging = !import.meta.env.PROD;

  const completedCount = specs.filter((s) => s.status === 'completed').length;
  const inProgressCount = specs.filter((s) => s.status === 'in-progress').length;
  const plannedCount = specs.filter((s) => s.status === 'planned').length;

  const getStatusTagVariant = (status: SpecEntry['status']): 'teal' | 'primary' | 'yellow' => {
    const variants: Record<SpecEntry['status'], 'teal' | 'primary' | 'yellow'> = {
      completed: 'teal',
      'in-progress': 'primary',
      planned: 'yellow',
    };
    return variants[status];
  };

  return (
    <div className="landing-base" style={pageStyle}>
      <h1 className="landing-h2">{t('pages.progress.title')}</h1>
      <p className="text-secondary" style={subtitleStyle}>
        {t('pages.progress.subtitle')}
      </p>

      {/* Stats Section */}
      <div style={statsContainerStyle}>
        <StatCard
          icon="✓"
          label={String(completedCount)}
          title={t('pages.progress.completed')}
          description=""
        />
        <StatCard
          icon="⚡"
          label={String(inProgressCount)}
          title={t('pages.progress.inProgress')}
          description=""
        />
        <StatCard
          icon="📋"
          label={String(plannedCount)}
          title={t('pages.progress.planned')}
          description=""
        />
      </div>

      {/* Specs Section */}
      <section style={sectionStyle}>
        <h2 className="landing-h3" style={sectionTitleStyle}>
          {t('pages.progress.specifications')}
        </h2>

        {specs.length === 0 ? (
          <p className="text-secondary" style={{ fontStyle: 'italic' }}>
            {t('pages.progress.noSpecs')}
          </p>
        ) : (
          <div style={specListStyle}>
            {specs.map((spec) => (
              <Card key={spec.specId} hoverable>
                <div style={specHeaderStyle}>
                  <code style={specIdStyle}>{spec.specId}</code>
                  <Tag variant={getStatusTagVariant(spec.status)}>{spec.status.toUpperCase()}</Tag>
                </div>
                <h3 className="landing-h3" style={{ margin: '0 0 16px 0' }}>
                  {spec.title}
                </h3>
                <div style={specDetailsStyle}>
                  <div style={detailRowStyle}>
                    <span
                      className="font-mono text-sm"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {t('pages.progress.demoPath')}
                    </span>
                    <code style={demoPathStyle}>{getFullDemoPath(spec, includeStaging)}</code>
                  </div>
                  {spec.completedAt && (
                    <div style={detailRowStyle}>
                      <span
                        className="font-mono text-sm"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {t('pages.progress.completedAt')}
                      </span>
                      <span>{spec.completedAt}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// Styles using design system
const pageStyle: React.CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '40px 20px 80px',
  backgroundColor: 'var(--color-background)',
  minHeight: '100vh',
};

const subtitleStyle: React.CSSProperties = {
  marginBottom: '32px',
};

const statsContainerStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '16px',
  marginBottom: '40px',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '32px',
};

const sectionTitleStyle: React.CSSProperties = {
  borderBottom: '2px solid var(--border-color)',
  paddingBottom: '8px',
  marginBottom: '16px',
};

const specListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const specHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
};

const specIdStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-yellow-light)',
  padding: '4px 8px',
  border: '1px solid var(--border-color)',
  fontFamily: 'var(--font-mono)',
  fontSize: '13px',
  color: 'var(--color-text-primary)',
};

const specDetailsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const detailRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
};

const demoPathStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-blue-light)',
  padding: '2px 8px',
  border: '1px solid var(--border-color)',
  fontFamily: 'var(--font-mono)',
  fontSize: '13px',
  color: 'var(--color-text-primary)',
};
